import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SiteHeader } from '../components/SiteHeader';
import { SEO } from '../components/SEO';

/**
 * Company Closure (DS01) enquiry flow.
 *
 * Structured fields up front so we have everything we need to quote
 * without a back-and-forth email. Closure is always priced by enquiry
 * because the right path depends on the company's specific situation
 * (creditors outstanding, recent trading activity, etc.), so this stays
 * a contact-form submission rather than a paid checkout.
 */
export function Closure() {
  const [form, setForm] = useState({
    companyNumber: '',
    companyName: '',
    directorName: '',
    email: '',
    phone: '',
    reason: '',
    lastTradedDate: '',
    outstandingObligations: '' as '' | 'no' | 'yes',
    obligationDetails: '',
    website: '', // honeypot
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.outstandingObligations) {
      setError('Please tell us whether the company has any outstanding obligations.');
      return;
    }
    setStatus('sending');
    setError(null);
    try {
      const message = [
        `[Company Closure (DS01) enquiry]`,
        ``,
        `Company number: ${form.companyNumber}`,
        `Company name:   ${form.companyName}`,
        `Director:       ${form.directorName}`,
        ``,
        `Reason for closure: ${form.reason}`,
        `Date last traded:   ${form.lastTradedDate || '(not provided)'}`,
        ``,
        `Outstanding creditors / employees / legal action? ${form.outstandingObligations === 'yes' ? 'YES' : 'NO'}`,
        form.outstandingObligations === 'yes' && form.obligationDetails
          ? `\nDetails:\n${form.obligationDetails}`
          : '',
      ].join('\n');

      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.directorName,
          email: form.email,
          phone: form.phone,
          companyName: form.companyName,
          message,
          website: form.website,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Failed (${res.status})`);
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Could not send. Try again.');
    }
  }

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO
        title="Company Closure (DS01) — Dormant Accounts Filing"
        description="Close your UK limited company (DS01 strike-off). Structured enquiry so we can quote without back-and-forth."
        path="/closure"
      />
      <SiteHeader />

      <main className="mx-auto max-w-3xl px-6 py-12">
        <Link to="/services" className="text-sm text-brand-700 hover:underline">
          ← Back to services
        </Link>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
          Close a company
        </h1>
        <p className="mt-3 text-slate-600">
          Voluntary strike-off (DS01) for a company you no longer need. Tell us a bit about the
          company below and we'll come back with a fixed-fee quote.
        </p>

        {status === 'sent' ? (
          <div className="mt-8 rounded-xl bg-green-50 p-8 ring-1 ring-green-200">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-2xl text-green-700">
              ✓
            </div>
            <h2 className="mt-4 text-xl font-semibold text-green-900">Enquiry received</h2>
            <p className="mt-2 text-sm text-green-800">
              Thanks. We'll review your details and come back with a fixed-fee quote and next
              steps, usually within a few hours.
            </p>
            <Link to="/services" className="btn-ghost mt-6 inline-flex">Back to services</Link>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="mt-8 space-y-6 rounded-xl bg-white p-6 ring-1 ring-slate-200 sm:p-8"
          >
            {/* Company */}
            <section>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Company
              </h2>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <Field label="Company number" required>
                  <input
                    type="text"
                    value={form.companyNumber}
                    onChange={set('companyNumber')}
                    required
                    pattern="[A-Za-z0-9]{6,10}"
                    className="input font-mono"
                    placeholder="12345678"
                  />
                </Field>
                <Field label="Company name" required>
                  <input
                    type="text"
                    value={form.companyName}
                    onChange={set('companyName')}
                    required
                    autoComplete="organization"
                    className="input"
                    placeholder="Acme Holdings Ltd"
                  />
                </Field>
              </div>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Director / contact
              </h2>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <Field label="Director name" required>
                  <input
                    type="text"
                    value={form.directorName}
                    onChange={set('directorName')}
                    required
                    autoComplete="name"
                    className="input"
                    placeholder="Jane Smith"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    value={form.email}
                    onChange={set('email')}
                    required
                    autoComplete="email"
                    className="input"
                    placeholder="jane@example.com"
                  />
                </Field>
                <Field label="Phone (optional)" className="sm:col-span-2">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={set('phone')}
                    autoComplete="tel"
                    className="input"
                    placeholder="07123 456 789"
                  />
                </Field>
              </div>
            </section>

            {/* Closure details */}
            <section>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Closure details
              </h2>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <Field label="Reason for closure" required>
                  <select
                    value={form.reason}
                    onChange={set('reason')}
                    required
                    className="input"
                  >
                    <option value="">Select a reason…</option>
                    <option>No longer needed</option>
                    <option>Business sold</option>
                    <option>Retiring</option>
                    <option>Replacing with a new company</option>
                    <option>Never traded</option>
                    <option>Other</option>
                  </select>
                </Field>
                <Field label="Date last traded">
                  <input
                    type="date"
                    value={form.lastTradedDate}
                    onChange={set('lastTradedDate')}
                    className="input"
                  />
                </Field>
              </div>

              <div className="mt-4">
                <span className="block text-sm font-medium text-slate-700">
                  Any outstanding obligations? <span className="text-amber-600">*</span>
                </span>
                <p className="mt-1 text-xs text-slate-500">
                  Outstanding creditors, employees, ongoing legal action, or tax liabilities. DS01
                  isn't suitable if any of these exist.
                </p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <RadioCard
                    selected={form.outstandingObligations === 'no'}
                    onClick={() => setForm((f) => ({ ...f, outstandingObligations: 'no', obligationDetails: '' }))}
                    title="No outstanding obligations"
                    sub="Company is clean to strike off"
                  />
                  <RadioCard
                    selected={form.outstandingObligations === 'yes'}
                    onClick={() => setForm((f) => ({ ...f, outstandingObligations: 'yes' }))}
                    title="Yes, there are some"
                    sub="We'll advise on the right route"
                  />
                </div>

                {form.outstandingObligations === 'yes' && (
                  <Field label="Tell us a bit more" className="mt-4">
                    <textarea
                      value={form.obligationDetails}
                      onChange={set('obligationDetails')}
                      rows={4}
                      className="input"
                      placeholder="e.g. one outstanding supplier invoice (£300), HMRC corporation tax due, etc."
                    />
                  </Field>
                )}
              </div>
            </section>

            <input
              type="text"
              name="website"
              value={form.website}
              onChange={set('website')}
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
            />

            {error && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
            )}

            <div className="flex flex-col items-center gap-3 border-t border-slate-100 pt-6">
              <button
                type="submit"
                disabled={status === 'sending'}
                className="btn-primary w-full justify-center sm:w-auto sm:min-w-[260px]"
              >
                {status === 'sending' ? 'Sending…' : 'Send enquiry'}
              </button>
              <p className="text-[11px] text-slate-500">
                We'll come back with a fixed-fee quote and the next steps by email.
              </p>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}

function Field({
  label,
  required,
  children,
  className = '',
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="text-amber-600"> *</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function RadioCard({
  selected,
  onClick,
  title,
  sub,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  sub: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border p-4 text-left transition ${
        selected
          ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-500'
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`flex h-4 w-4 items-center justify-center rounded-full border ${
            selected ? 'border-brand-600 bg-brand-600' : 'border-slate-300'
          }`}
        >
          {selected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
        </span>
        <span className="font-medium text-slate-900">{title}</span>
      </div>
      <p className="mt-1 text-xs text-slate-500">{sub}</p>
    </button>
  );
}
